import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MydataPageShell } from './MydataPageShell';
import {
  Button,
  Callout,
  Label,
  LoadingSpinner,
  SaveIcon,
  Select,
  TextInput,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type { MydataConnection, MydataEnvironment } from '../../types/mydata';
import { formatDateTime } from '../../utils/format';

const AADE_REGISTER_URLS: Record<MydataEnvironment, string> = {
  sandbox: 'https://mydata-dev-register.azurewebsites.net/',
  production: 'https://www1.aade.gr/saadeapps2/bookkeeper-web',
};

export function MydataConnectionPage() {
  const labels = t().mydata;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [connection, setConnection] = useState<MydataConnection | null>(null);
  const [aadeUserId, setAadeUserId] = useState('');
  const [subscriptionKey, setSubscriptionKey] = useState('');
  const [environment, setEnvironment] =
    useState<MydataEnvironment>('sandbox');

  const registerUrl = AADE_REGISTER_URLS[environment];
  const registerHint =
    environment === 'production'
      ? labels.registerLinkProduction
      : labels.registerLinkSandbox;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getMydataConnection();
      setConnection(data);
      setAadeUserId(data.aadeUserId ?? '');
      setEnvironment(data.environment);
      setSubscriptionKey('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: {
        aadeUserId: string;
        subscriptionKey?: string;
        environment: MydataEnvironment;
      } = {
        aadeUserId: aadeUserId.trim(),
        environment,
      };
      if (subscriptionKey.trim()) {
        payload.subscriptionKey = subscriptionKey.trim();
      }
      const data = await api.upsertMydataConnection(payload);
      setConnection(data);
      setSubscriptionKey('');
      setMessage(labels.connectionSaved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <MydataPageShell>
        <LoadingSpinner />
      </MydataPageShell>
    );
  }

  return (
    <MydataPageShell>
      <div className="flex shrink-0 items-center justify-between gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {labels.connectionHint}
        </p>
        <Link
          to="/mydata"
          className="shrink-0 text-sm font-medium text-slate-600 underline-offset-2 hover:underline dark:text-slate-300"
        >
          {labels.periodTitle}
        </Link>
      </div>

      <Callout variant="info" className="shrink-0">
        {labels.securityCallout}
      </Callout>

      {error && (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      )}
      {message && (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      )}
      {connection?.lastSyncError && (
        <Callout variant="warning" className="shrink-0">
          {labels.lastSyncError}: {connection.lastSyncError}
        </Callout>
      )}

      <div className="grid shrink-0 gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:grid-cols-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {labels.tenantName}
          </div>
          <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
            {connection?.tenantName || labels.emptyValue}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {labels.vatNumber}
          </div>
          <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
            {connection?.vatNumber || labels.missingVat}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {labels.subscriptionKey}
          </div>
          <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
            {connection?.hasSubscriptionKey
              ? labels.subscriptionKeySet
              : labels.subscriptionKeyMissing}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {labels.lastSync}
          </div>
          <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
            {connection?.lastSyncAt
              ? formatDateTime(connection.lastSyncAt)
              : labels.neverSynced}
          </div>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="max-w-xl shrink-0 space-y-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
      >
        <div>
          <Label htmlFor="aadeUserId">{labels.aadeUserId}</Label>
          <TextInput
            id="aadeUserId"
            value={aadeUserId}
            onChange={(e) => setAadeUserId(e.target.value)}
            required
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="subscriptionKey">{labels.subscriptionKey}</Label>
          <TextInput
            id="subscriptionKey"
            type="password"
            value={subscriptionKey}
            onChange={(e) => setSubscriptionKey(e.target.value)}
            autoComplete="new-password"
            placeholder={
              connection?.hasSubscriptionKey
                ? labels.subscriptionKeyHint
                : undefined
            }
          />
          {connection?.hasSubscriptionKey && (
            <p className="mt-1 text-xs text-slate-500">
              {labels.subscriptionKeyHint}
            </p>
          )}
        </div>
        <div>
          <Label>{labels.environment}</Label>
          <Select
            value={environment}
            onChange={(e) =>
              setEnvironment(e.target.value as MydataEnvironment)
            }
            options={[
              { value: 'sandbox', label: labels.sandbox },
              { value: 'production', label: labels.production },
            ]}
          />
          <p className="mt-2 text-sm">
            <a
              href={registerUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-sky-700 underline underline-offset-2 hover:text-sky-800 dark:text-sky-400"
            >
              {labels.registerLink}
            </a>
            <span className="mt-1 block text-xs text-slate-500">
              {registerHint}
            </span>
          </p>
        </div>
        <Button type="submit" disabled={saving}>
          <SaveIcon className="h-4 w-4" />
          {labels.saveConnection}
        </Button>
      </form>
    </MydataPageShell>
  );
}
